import React, { useState, useEffect, useRef, useMemo, Suspense } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  NativeScrollEvent,
  NativeSyntheticEvent,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter, useLocalSearchParams } from 'expo-router';
import * as Haptics from 'expo-haptics';
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated';
import Svg, { Path } from 'react-native-svg';
// P06-4: react-native-marked lazy-loaded so it is excluded from the initial
// JS bundle chunk and only fetched when the trail step screen first mounts.
const Markdown = React.lazy(() => import('react-native-marked'));
import { useAuth } from '@/contexts/AuthContext';
import { apiService } from '@/services/apiService';
import PrimaryButton from '@/components/PrimaryButton';
import { useAudioPlayer } from '@/contexts/AudioPlayerContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useServerVerifiedPlus } from '@/hooks/useEntitlement';
import RevenueCatService from '@/services/revenueCatService';
import { prefetchAudioInBackground } from '@/services/audioPrefetchService';

interface StepDetail {
  step_number: number;
  title: string;
  subtitle: string | null;
  content: string | null;
  audio_url?: string | null;
  is_unlocked: boolean;
  is_completed: boolean;
  scroll_percentage: number;
}

interface HeadingTopic {
  id: string;
  label: string;
  index: number;
  yPosition?: number;
}

const GemIconStep = () => (
  <Svg width="14" height="14" viewBox="0 0 12 12" fill="none">
    <Path
      d="M1.75524 5.7637L5.40815 9.7771C5.72535 10.1259 6.2741 10.1259 6.59125 9.7771L10.2447 5.7632C10.5502 5.42805 10.5852 4.92698 10.3298 4.55191L8.8587 2.39099C8.6763 2.12343 8.37275 1.96289 8.04875 1.96289H3.95555C3.63156 1.96289 3.32849 2.12294 3.14606 2.3905L1.67059 4.55191C1.4147 4.92649 1.44973 5.42805 1.75524 5.7637Z"
      stroke="white"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M6.99847 1.96289L7.68197 4.55191C7.78117 4.92698 7.76757 5.42805 7.64887 5.7632L6.22887 9.7771C6.10577 10.1259 5.89222 10.1259 5.76912 9.7771L4.34912 5.7637C4.23042 5.42805 4.2168 4.92649 4.31653 4.55191L5.00197 1.96289"
      stroke="white"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path d="M1.50391 5.10156H10.4954" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

export default function SlugStepDetailScreen() {
  const router = useRouter();
  const { t, language } = useLanguage();
  const { slug, stepNumber, trailName } = useLocalSearchParams<{ slug: string; stepNumber: string; trailName?: string }>();
  const { session, sessionReady, user } = useAuth();
  const scrollViewRef = useRef<ScrollView>(null);

  const { playTrack, isPlaying, currentTrack: audioCurrentTrack } = useAudioPlayer();
  const { hasPadrinhoPlus, isLoading: plusLoading } = useServerVerifiedPlus();

  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState<StepDetail | null>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [canComplete, setCanComplete] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [isIndexOpen, setIsIndexOpen] = useState(false);
  const [headingTopics, setHeadingTopics] = useState<HeadingTopic[]>([]);
  const [headingRefs, setHeadingRefs] = useState<Record<string, number>>({});

  useEffect(() => {
    // PV2-16 (CL-P03): session?.access_token removed from deps — token rotation
    // must not trigger a data refetch. Only step identity, user, and language matter.
    if (user && sessionReady && stepNumber && slug) {
      // PRD §5.8 — `trail_step_started`. Fires once per mount (when the
      // user opens the step detail page). `step_id` is composed
      // `${slug}/${stepIdx}` to give analysts a stable cross-trail key.
      // Clarity dedupes within a session.
      const stepIdx = parseInt(stepNumber as string);
      if (Number.isFinite(stepIdx)) {
        import('@/utils/clarityEvents')
          .then(({ logTrailStepStarted }) =>
            logTrailStepStarted({ step_id: `${String(slug)}/${stepIdx}` })
          )
          .catch(() => {});
      }
      loadStepData();
      trackStepStart();
    }
  }, [stepNumber, slug, user, sessionReady, language]);

  // Audio prefetch — Plus users only. Two parallel jobs once the step loads:
  //   1. Current step's audio → user hears 0s wait when they tap play
  //   2. Step N+1's audio → next step is already cached when they advance
  // Both writes go to the persistent disk cache, so this only does network
  // work the first time per audio file. Free users skip entirely (they hit
  // the paywall on tap, no point burning their data).
  useEffect(() => {
    if (!step?.audio_url || !hasPadrinhoPlus || !slug) return;

    prefetchAudioInBackground(step.audio_url);

    const nextStepNumber = step.step_number + 1;
    apiService
      .get(`/api/trails/${slug}/steps/${nextStepNumber}`)
      .then((resp) => {
        const nextAudio = resp?.data?.step?.audio_url;
        if (nextAudio) prefetchAudioInBackground(nextAudio);
      })
      .catch(() => {
        // Lookahead is best-effort — silently ignore (last step / 404 / network).
      });
  }, [step?.audio_url, step?.step_number, hasPadrinhoPlus, slug]);

  const loadStepData = async () => {
    try {
      if (!stepNumber || !slug || !user) return;

      const stepNum = parseInt(stepNumber as string);
      console.log(`📊 Loading trail [${slug}] step ${stepNum} via Elo API...`);

      const response = await apiService.get(`/api/trails/${slug}/steps/${stepNum}`);

      if (!response.success) {
        console.error('Error loading step from API:', response.error);
        return;
      }

      const stepData = response.data?.step;
      if (stepData) {
        if (!stepData.is_unlocked) {
          console.warn('Step is locked, going back');
          router.back();
          return;
        }
        setStep(stepData);
        extractHeadings(stepData.content);
        if (stepData.is_completed) setCanComplete(true);
      }
    } catch (error) {
      console.error('Error loading step:', error);
    } finally {
      setLoading(false);
    }
  };

  const extractHeadings = (content: string) => {
    const h1Regex = /^#\s+(.+)$/gm;
    const h2Regex = /^##\s+(.+)$/gm;
    const h3Regex = /^###\s+(.+)$/gm;

    const topics: HeadingTopic[] = [];
    let match;
    let index = 1;

    while ((match = h1Regex.exec(content)) !== null) {
      topics.push({ id: `heading-${index}`, label: match[1].trim().replace(/\*\*/g, ''), index });
      index++;
    }
    if (topics.length === 0) {
      index = 1;
      while ((match = h2Regex.exec(content)) !== null) {
        topics.push({ id: `heading-${index}`, label: match[1].trim().replace(/\*\*/g, ''), index });
        index++;
      }
    }
    if (topics.length === 0) {
      index = 1;
      while ((match = h3Regex.exec(content)) !== null) {
        topics.push({ id: `heading-${index}`, label: match[1].trim().replace(/\*\*/g, ''), index });
        index++;
      }
    }
    setHeadingTopics(topics);
  };

  const trackStepStart = async () => {
    try {
      if (!user || !stepNumber || !slug) return;
      const stepNum = parseInt(stepNumber as string);
      await apiService.post('/api/trail/progress', {
        trail_slug: slug,
        step_number: stepNum,
        scroll_percentage: 0,
      });
    } catch (error) {
      console.error('Error tracking step start:', error);
    }
  };

  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
    const maxScroll = contentSize.height - layoutMeasurement.height;
    const percentage = maxScroll > 0 ? (contentOffset.y / maxScroll) * 100 : 100;

    setScrollProgress(Math.min(Math.round(percentage), 100));

    if (percentage >= 80 && !canComplete) {
      setCanComplete(true);
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
    }
    setShowScrollToTop(contentOffset.y > 200);
  };

  const scrollToTop = () => {
    scrollViewRef.current?.scrollTo({ y: 0, animated: true });
    setShowScrollToTop(false);
  };

  // `afterComplete` controls where the user lands once the completion
  // succeeds. Default 'back' preserves the legacy "Completar leitura"
  // PrimaryButton behaviour (pop to the step list). 'rate' is used by
  // the new "Avaliar este passo" CSAT card below the PrimaryButton —
  // tapping it completes the step AND forwards the user to the trail-
  // contextualised CSAT screen, so a single tap captures both signals.
  // Both paths share the same network call, Clarity event, and diary
  // entry so the step is recorded as completed regardless of which
  // affordance the user chose.
  const handleCompleteStep = async (afterComplete: 'back' | 'rate' = 'back') => {
    if (!canComplete || completing || !user || !stepNumber || !slug) return;

    try {
      setCompleting(true);
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);

      const stepNum = parseInt(stepNumber as string);
      const response = await apiService.post('/api/trail/progress', {
        trail_slug: slug,
        step_number: stepNum,
        scroll_percentage: 100,
        is_completed: true,
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to complete step');
      }

      // RJ-3b — invalidate today's recovery plan so the Missões row for
      // `progredir_trilha` flips to completed on next visit. Dynamic import
      // matches the pattern used by the other writer hooks.
      import('@/services/recoveryPlanService')
        .then(({ recoveryPlanService }) => recoveryPlanService.invalidateToday())
        .catch(() => {});

      // PRD §5.8 — `trail_step_completed`. Success branch only — failed
      // completes throw above and skip the event. `step_id` matches the
      // `started` event so funnel analytics can pair them.
      import('@/utils/clarityEvents')
        .then(({ logTrailStepCompleted }) =>
          logTrailStepCompleted({ step_id: `${String(slug)}/${stepNum}` })
        )
        .catch(() => {});

      try {
        const displayTrailName = trailName ? decodeURIComponent(trailName as string) : (slug as string);
        const stepTitle = step?.title || `${t('trail.step')} ${stepNum}`;
        const completionMessage = t('trail.congratulations.completedStepMessage', { stepTitle, trailName: displayTrailName });
        const noteContent = `${displayTrailName}\n${completionMessage}`;
        await apiService.post('/api/diary/notes', { content: noteContent, type: 'trail' });
      } catch (diaryError) {
        console.error('Error creating diary entry for trail completion:', diaryError);
      }

      // 500ms delay matches the existing UX cadence (lets the haptic +
      // button success state register before navigating away).
      // For the 'rate' path we use replace() — the trail step page is
      // logically "done" so we don't want it on the back stack behind
      // the CSAT screen; the CSAT screen's own safeBack falls back to
      // /diary if there's nothing to pop, which is the correct
      // landing for a completed step.
      setTimeout(() => {
        if (afterComplete === 'rate') {
          router.replace((`/csat?source=trail_step&returnTo=${encodeURIComponent('/trail/' + slug)}`) as any);
        } else {
          router.back();
        }
      }, 500);
    } catch (error) {
      console.error('Error completing step:', error);
      setCompleting(false);
    }
  };

  const handleCompleteAndRate = () => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    handleCompleteStep('rate');
  };

  const handleExit = () => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    router.back();
  };

  const handleAudioCardPress = async () => {
    if (!step) return;
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);

    // While the entitlement status is still loading (default: hasPadrinhoPlus
    // = false), defer the gate decision. Acting on the loading default would
    // falsely paywall a real Plus user who taps within the ~hundreds-of-ms
    // resolution window. Just consume the haptic and let them tap again once
    // status is verified — the visual defaults to the gated card so a free
    // user is never misled, and the brief no-op is invisible to most users.
    if (plusLoading) return;

    // Nano: capture intent on every tap, before the gate decision. The
    // `variant` lets analysts split "Plus user wanted audio" vs "free user
    // hit the paywall" without standing up two surfaces. The downstream
    // `plus_upgrade_cta` nano fires automatically from the paywall service
    // for the gated branch (Phase 3 instrumentation in revenueCatService).
    import('@/utils/clarityEvents')
      .then(({ logUiAction }) =>
        logUiAction({
          surface: 'trail_step',
          element: 'audio_play',
          action: 'tapped',
          variant: hasPadrinhoPlus ? 'plus' : 'gated',
        })
      )
      .catch(() => {});

    if (!hasPadrinhoPlus) {
      await RevenueCatService.presentPaywall({ fromSurface: 'trail_step' });
      return;
    }

    if (audioCurrentTrack?.stepNumber === step.step_number && isPlaying) {
      router.push('/audio-player');
      return;
    }
    if (!step.audio_url) return;
    await playTrack({ stepNumber: step.step_number, stepTitle: step.title, audioUrl: step.audio_url });
    router.push('/audio-player');
  };

  const handleTopicPress = (topicId: string) => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    const yPosition = headingRefs[topicId];
    if (yPosition !== undefined && scrollViewRef.current) {
      scrollViewRef.current.scrollTo({ y: yPosition - 80, animated: true });
      setIsIndexOpen(false);
    }
  };

  const isCurrentStepPlaying = audioCurrentTrack?.stepNumber === step?.step_number && isPlaying;

  // react-native-marked v7: `theme` only accepts `colors` and `spacing`.
  // Per-element overrides go through the SEPARATE `styles` prop, with keys
  // matching the `MarkedStyles` interface (`li`, `code`, `codespan`,
  // `text`, etc. — not `listItem` / `codeBlock` / `codeInline`). The
  // earlier `theme.styles` + `theme.fonts` block was being ignored, which
  // is why body text never moved off the library default.
  const markdownTheme = useMemo(() => ({
    colors: { background: '#FFFFFF', text: '#151515', link: '#015480', border: '#E2E8F0', code: '#F0EEE4' },
  }), []);

  const markdownStyles = useMemo(() => StyleSheet.create({
    text: { fontFamily: 'InstrumentSans_400Regular', fontSize: 16, color: '#151515', lineHeight: 24 },
    paragraph: { marginBottom: 12 },
    // react-native-marked's defaults give h1/h2 a 1px borderBottom plus
    // `paddingBottom: mdSpacing.s`. Override ALL three (width, color,
    // padding) so no residual underline or extra padding survives the
    // StyleSheet.flatten merge in `node_modules/react-native-marked/.../theme/styles.js`.
    h1: { fontFamily: 'InstrumentSans_700Bold', fontSize: 28, color: '#151515', lineHeight: 34, marginTop: 16, marginBottom: 12, borderBottomWidth: 0, borderBottomColor: 'transparent', paddingBottom: 0 },
    h2: { fontFamily: 'InstrumentSans_700Bold', fontSize: 22, color: '#151515', lineHeight: 28, marginTop: 16, marginBottom: 12, borderBottomWidth: 0, borderBottomColor: 'transparent', paddingBottom: 0 },
    h3: { fontFamily: 'InstrumentSans_600SemiBold', fontSize: 18, color: '#151515', lineHeight: 24, marginTop: 16, marginBottom: 12, borderBottomWidth: 0, borderBottomColor: 'transparent', paddingBottom: 0 },
    h4: { fontFamily: 'InstrumentSans_600SemiBold', fontSize: 16, color: '#151515', lineHeight: 22, marginTop: 12, marginBottom: 8, borderBottomWidth: 0, borderBottomColor: 'transparent', paddingBottom: 0 },
    h5: { fontFamily: 'InstrumentSans_600SemiBold', fontSize: 14, color: '#151515', lineHeight: 20, marginTop: 12, marginBottom: 8, borderBottomWidth: 0, borderBottomColor: 'transparent', paddingBottom: 0 },
    h6: { fontFamily: 'InstrumentSans_500Medium', fontSize: 14, color: '#757C85', lineHeight: 20, marginTop: 12, marginBottom: 8, borderBottomWidth: 0, borderBottomColor: 'transparent', paddingBottom: 0 },
    strong: { fontFamily: 'InstrumentSans_700Bold' },
    em: { fontFamily: 'InstrumentSans_400Regular_Italic' },
    link: { color: '#015480', textDecorationLine: 'underline' as const },
    li: { fontFamily: 'InstrumentSans_400Regular', fontSize: 16, lineHeight: 24, color: '#151515', marginVertical: 4 },
    blockquote: { borderLeftWidth: 4, borderLeftColor: '#015480', paddingLeft: 16, marginVertical: 12 },
    codespan: { backgroundColor: '#F0EEE4', color: '#015480', fontFamily: 'Courier', fontSize: 13, paddingVertical: 2, paddingHorizontal: 6, borderRadius: 4 },
    code: { backgroundColor: '#F9F8F3', padding: 16, marginVertical: 12, borderRadius: 8 },
    hr: { height: 1, backgroundColor: '#E2E8F0', borderBottomWidth: 0, marginVertical: 24 },
  }), []);

  // Pre-process the markdown so every heading that is NOT the first
  // non-empty line of the document is preceded by an `hr` divider. We
  // replace the library's default heading underline with a divider
  // ABOVE the heading (which sits between the previous paragraph and
  // the heading itself). The first heading is left alone — there is
  // no previous content to separate it from.
  const processedContent = useMemo(() => {
    if (!step?.content) return '';
    const lines = step.content.split('\n');
    const out: string[] = [];
    let seenContent = false;
    for (const line of lines) {
      const isHeading = /^#{1,6}\s+/.test(line);
      const isBlank = line.trim() === '';
      if (isHeading && seenContent) {
        // Drop trailing blanks so the divider sits flush, then insert it.
        while (out.length && out[out.length - 1].trim() === '') out.pop();
        out.push('', '---', '', line);
      } else {
        out.push(line);
      }
      if (!isBlank) seenContent = true;
    }
    return out.join('\n');
  }, [step?.content]);

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <View style={styles.headerGroup}>
        <View style={styles.header}>
          <TouchableOpacity onPress={handleExit} style={styles.headerLeft}>
            <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <Path d="M15 18L9 12L15 6" stroke="#151515" strokeWidth="2" strokeLinecap="square" />
            </Svg>
          </TouchableOpacity>
          <View style={styles.headerCenter}>
            {loading ? (
              <View style={styles.headerSubtitleSkeleton} />
            ) : (
              <Text style={styles.headerTitle} numberOfLines={1}>{`${t('trail.step')} ${step?.step_number ?? stepNumber}`}</Text>
            )}
          </View>
          <View style={styles.headerRight} />
        </View>

        <View style={styles.progressBarWrapper}>
          <View style={styles.progressBarContainer}>
            <View style={[styles.progressBarFill, { width: `${scrollProgress}%` }]} />
          </View>
        </View>
      </View>

      <ScrollView
        ref={scrollViewRef}
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        onScroll={handleScroll}
        scrollEventThrottle={16}
      >
        {loading ? (
          <>
            <View style={styles.contentSkeleton}>
              {[100, 95, 98, 92, 100, 88, 96, 90].map((w, i) => (
                <View key={i} style={[styles.skeletonBox, { width: `${w}%`, height: 16, borderRadius: 6, marginBottom: 12 }]} />
              ))}
            </View>
            <View style={styles.bottomSection}>
              <View style={[styles.skeletonBox, { width: '100%', height: 48, borderRadius: 70 }]} />
            </View>
          </>
        ) : step ? (
          <>
            <Suspense fallback={null}>
              <Markdown
                value={processedContent}
                flatListProps={{ scrollEnabled: false }}
                theme={markdownTheme}
                styles={markdownStyles}
              />
            </Suspense>

            {isIndexOpen && headingTopics.length > 0 && (
              <View style={styles.inlineIndexContainer}>
                <Text style={styles.inlineIndexTitle}>{t('trail.index')}</Text>
                {headingTopics.map((topic) => (
                  <TouchableOpacity
                    key={topic.id}
                    style={styles.inlineIndexTopic}
                    onPress={() => handleTopicPress(topic.id)}
                    activeOpacity={0.7}
                  >
                    <Text style={styles.inlineIndexTopicText}>{topic.index}. {topic.label}</Text>
                    <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <Path d="M5.83203 3.3335L10.4987 8.00016L5.83203 12.6668" stroke="#151515" strokeWidth="2" strokeLinecap="square" />
                    </Svg>
                  </TouchableOpacity>
                ))}
              </View>
            )}

            <View style={styles.bottomSection}>
              <PrimaryButton
                title={canComplete ? t('trail.actions.completeReading') : `${t('trail.actions.scrollToEnd')} (${scrollProgress}%)`}
                onPress={() => handleCompleteStep('back')}
                disabled={!canComplete}
                loading={completing}
              />
              {!canComplete && (
                <Text style={styles.completeHint}>{t('trail.hints.readToComplete')}</Text>
              )}

              {/* Trail-context CSAT entry — visually a tertiary button
                  (transparent, h:48, matches `components/TertiaryButton`)
                  with a leading star icon. Built inline rather than via
                  TertiaryButton because that component doesn't support
                  icons today. Reuses the same /csat screen but adds
                  `source=trail_step` so the screen swaps to trail-
                  specific topic chips (Content + App). Only rendered
                  once the step is markable as complete: tapping it
                  both completes the step AND opens the rating screen,
                  so a single tap captures both signals. */}
              {canComplete && (
                <TouchableOpacity
                  style={styles.csatButton}
                  onPress={handleCompleteAndRate}
                  disabled={completing}
                  activeOpacity={0.8}
                  accessibilityRole="button"
                  accessibilityLabel={t('trail.csat.title')}
                >
                  <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <Path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M10.9202 3.48068L12.4431 6.52297C12.5923 6.82163 12.8804 7.02873 13.2145 7.07666L16.6211 7.56702C17.4628 7.68854 17.7977 8.70863 17.1886 9.29312L14.7252 11.6602C14.4831 11.893 14.3729 12.2276 14.4301 12.5562L15.0115 15.898C15.1547 16.7247 14.2748 17.3554 13.5225 16.9643L10.4777 15.3854C10.1792 15.2305 9.82168 15.2305 9.52232 15.3854L6.4775 16.9643C5.72519 17.3554 4.84533 16.7247 4.98937 15.898L5.56987 12.5562C5.62714 12.2276 5.51694 11.893 5.27485 11.6602L2.8114 9.29312C2.20226 8.70863 2.5372 7.68854 3.37889 7.56702L6.78554 7.07666C7.11961 7.02873 7.40856 6.82163 7.55781 6.52297L9.07979 3.48068C9.45638 2.72845 10.5436 2.72845 10.9202 3.48068Z"
                      stroke="#151515"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </Svg>
                  <Text style={styles.csatButtonText}>{t('trail.csat.title')}</Text>
                </TouchableOpacity>
              )}
            </View>
          </>
        ) : null}
      </ScrollView>

      {!loading && step?.audio_url && (
        <View style={styles.floatingPlayerContainer}>
          {showScrollToTop && (
            <Animated.View entering={FadeIn.duration(200)} exiting={FadeOut.duration(200)} style={styles.scrollToTopWrapper}>
              <TouchableOpacity style={styles.scrollToTopButton} onPress={scrollToTop} activeOpacity={0.8}>
                <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <Path d="M8.16797 3.3999V13" stroke="white" strokeWidth="1.5" strokeLinecap="square" />
                  <Path d="M12.1811 7.03331L8.16509 3L4.14844 7.03331" stroke="white" strokeWidth="1.5" strokeLinecap="square" />
                </Svg>
              </TouchableOpacity>
            </Animated.View>
          )}

          <TouchableOpacity
            style={styles.playerBar}
            onPress={handleAudioCardPress}
            activeOpacity={0.7}
            accessibilityRole="button"
            accessibilityLabel={
              !hasPadrinhoPlus
                ? t('trail.accessibility.audioPlusGated')
                : isCurrentStepPlaying
                  ? t('trail.accessibility.pausePlayer')
                  : t('trail.accessibility.listenStep')
            }
          >
            {hasPadrinhoPlus && isCurrentStepPlaying ? (
              <Svg width="24" height="24" viewBox="0 0 47 47" fill="none">
                <Path d="M11.6055 12.5708C11.6055 11.1681 12.7426 10.031 14.1453 10.031H15.8011C17.2038 10.031 18.3409 11.1681 18.3409 12.5708V34.1054C18.3409 35.5081 17.2038 36.6452 15.8011 36.6452H14.1453C12.7426 36.6452 11.6055 35.5081 11.6055 34.1054V12.5708Z" fill="#151515" />
                <Path d="M27.0703 12.5708C27.0703 11.1681 28.2074 10.031 29.6101 10.031H31.2659C32.6686 10.031 33.8057 11.1681 33.8057 12.5708V34.1054C33.8057 35.5081 32.6686 36.6452 31.2659 36.6452H29.6101C28.2074 36.6452 27.0703 35.5081 27.0703 34.1054V12.5708Z" fill="#151515" />
              </Svg>
            ) : (
              <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <Path fillRule="evenodd" clipRule="evenodd" d="M6 6.56108L6 17.439C6 18.6355 7.22972 19.3865 8.21781 18.792L17.2537 13.3572C18.2483 12.759 18.249 11.2509 17.2544 10.6519L8.21853 5.20805C7.23044 4.6136 6 5.36385 6 6.56108Z" fill="#151515" />
              </Svg>
            )}

            <View style={styles.playerTextContainer}>
              <Text style={styles.playerActionText}>
                {hasPadrinhoPlus && isCurrentStepPlaying ? t('trail.actions.listening') : t('trail.actions.listen')}
              </Text>
              <Text style={styles.playerStepText} numberOfLines={1}>
                {t('trail.audio.stepLabel', { number: step.step_number.toString().padStart(2, '0') })}
              </Text>
            </View>

            {hasPadrinhoPlus ? (
              <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <Path d="M5.25 15.5L12.25 8.5L19.25 15.5" stroke="#151515" strokeWidth="2" strokeLinecap="square" />
              </Svg>
            ) : (
              <View style={styles.plusBadge}>
                <GemIconStep />
                <Text style={styles.plusBadgeText}>Plus</Text>
              </View>
            )}
          </TouchableOpacity>
        </View>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#FFFFFF' },
  headerGroup: { backgroundColor: '#FFFFFF' },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 24, paddingVertical: 16, height: 56 },
  headerLeft: { width: 32, height: 32, justifyContent: 'center', alignItems: 'center' },
  headerCenter: { flex: 1, alignItems: 'center', paddingHorizontal: 8 },
  headerRight: { width: 32, height: 32, justifyContent: 'center', alignItems: 'center' },
  headerTitle: { fontFamily: 'Inter_700Bold', fontSize: 14, color: '#151515', lineHeight: 14 },
  progressBarWrapper: { paddingHorizontal: 24 },
  progressBarContainer: { width: '100%', height: 2, backgroundColor: '#F9F8F3', overflow: 'hidden' },
  progressBarFill: { height: '100%', backgroundColor: '#151515' },
  scrollView: { flex: 1, backgroundColor: '#FFFFFF' },
  scrollContent: { padding: 24 },
  bottomSection: { marginTop: 24, marginBottom: 120, gap: 12 },
  completeHint: { fontFamily: 'Inter_400Regular', fontSize: 12, color: '#757C85', textAlign: 'center', lineHeight: 16.8 },
  // Tertiary-button look (transparent, h:48, gap:12, rounded 12) lifted
  // from `components/TertiaryButton.tsx` so the CSAT entry visually
  // matches the rest of the design system. Built inline because that
  // component doesn't accept a leading icon today; promote both into
  // an `icon` prop on TertiaryButton if a third surface needs the same
  // pattern.
  csatButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    gap: 12,
    borderRadius: 12,
    backgroundColor: 'transparent',
    height: 48,
    width: '100%',
  },
  csatButtonText: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    color: '#151515',
    lineHeight: 20,
    textAlign: 'center',
    fontWeight: '500',
  },
  contentSkeleton: { gap: 12 },
  skeletonBox: { backgroundColor: '#F0EEE4', opacity: 0.6 },
  floatingPlayerContainer: { position: 'absolute', bottom: 0, left: 0, right: 0, padding: 24, gap: 12 },
  scrollToTopWrapper: { alignSelf: 'flex-end' },
  scrollToTopButton: { width: 32, height: 32, borderRadius: 27, backgroundColor: '#151515', alignItems: 'center', justifyContent: 'center', shadowColor: '#000', shadowOffset: { width: 4, height: 4 }, shadowOpacity: 0.06, shadowRadius: 4, elevation: 4 },
  playerBar: { flexDirection: 'row', alignItems: 'center', gap: 16, padding: 16, borderRadius: 12, borderWidth: 1, borderColor: '#C0D1DB', backgroundColor: '#E6EBF0', shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.05, shadowRadius: 14, elevation: 4 },
  playerTextContainer: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8, height: 20, overflow: 'hidden' },
  playerActionText: { fontFamily: 'Inter_700Bold', fontSize: 14, color: '#151515', lineHeight: 19.6 },
  playerStepText: { flex: 1, fontFamily: 'Inter_400Regular', fontSize: 14, color: '#7E7D75', lineHeight: 19.6 },
  plusBadge: { flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: '#669AB7', borderRadius: 8, paddingHorizontal: 8, paddingVertical: 5, flexShrink: 0 },
  plusBadgeText: { fontFamily: 'Inter_700Bold', fontSize: 11, color: '#FFFFFF', lineHeight: 13 },
  inlineIndexContainer: { backgroundColor: '#FFFFFF', borderTopWidth: 1, borderTopColor: '#E7EAED', padding: 24, marginTop: 32, marginBottom: 24, gap: 24 },
  inlineIndexTitle: { fontFamily: 'Inter_700Bold', fontSize: 14, color: '#151515', lineHeight: 14 },
  inlineIndexTopic: { flexDirection: 'row', alignItems: 'center', gap: 8, width: '100%' },
  inlineIndexTopicText: { flex: 1, fontFamily: 'Sentient-Regular', fontSize: 16, color: '#151515', lineHeight: 22.4 },
  headerSubtitleSkeleton: { width: 120, height: 14, backgroundColor: '#F0EEE4', opacity: 0.6, borderRadius: 4 },
});
